import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import client from "@/api/client";
import { useSiteContent } from "@/context/SiteContentContext";
import {
  Loader2,
  Search,
  Save,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ExternalLink,
  Upload,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  pass: { icon: CheckCircle2, className: "text-emerald-600 bg-emerald-500/10 border-emerald-500/25" },
  warn: { icon: AlertTriangle, className: "text-amber-600 bg-amber-500/10 border-amber-500/25" },
  fail: { icon: XCircle, className: "text-rose-600 bg-rose-500/10 border-rose-500/25" },
};

function CharCount({ value, lo, hi }) {
  const n = (value || "").trim().length;
  const ok = n >= lo && n <= hi;
  return (
    <span className={`text-[10px] font-mono ${n === 0 ? "text-muted-foreground" : ok ? "text-emerald-600" : "text-amber-600"}`} dir="ltr">
      {n}/{hi}
    </span>
  );
}

function HealthRow({ check, t }) {
  const meta = STATUS_META[check.status] || STATUS_META.warn;
  const Icon = meta.icon;
  // Backend ships English fallbacks plus a translatable key + params.
  const label = t(`seo.check_${check.id}`, { defaultValue: check.label });
  const detail = check.detail_key
    ? t(`seo.detail_${check.detail_key}`, { ...(check.params || {}), defaultValue: check.detail })
    : check.detail;
  return (
    <li className="flex items-start gap-2.5 py-2 border-b border-border last:border-0" data-testid={`seo-check-${check.id}`}>
      <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${meta.className}`}>
        <Icon className="h-3 w-3" />
      </span>
      <div className="min-w-0">
        <p className="text-sm font-medium leading-tight">{label}</p>
        {detail && <p className="text-[11px] text-muted-foreground mt-0.5 break-words">{detail}</p>}
      </div>
    </li>
  );
}

/**
 * AdminSeoPanel — CMS-managed SEO for the homepage/site plus a live health
 * report (title/description lengths, missing page metas, robots + sitemap
 * reachability) so an admin can see search visibility without leaving the app.
 */
export function AdminSeoPanel() {
  const { t } = useTranslation();
  const { content, refresh } = useSiteContent();
  const [seo, setSeo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [health, setHealth] = useState(null);
  const [checking, setChecking] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (content?.seo) setSeo({ ...content.seo });
  }, [content?.seo]);

  const loadHealth = useCallback(async () => {
    setChecking(true);
    try {
      const res = await client.get("/admin/seo/health");
      setHealth(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setChecking(false);
    }
  }, [t]);

  useEffect(() => { loadHealth(); }, [loadHealth]);

  const set = (key, value) => setSeo((s) => ({ ...s, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      await client.put("/admin/content", { seo });
      await refresh();
      await loadHealth();
      toast.success(t("admin.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  const uploadOg = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await client.post("/admin/upload-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("og_image_url", res.data.url);
      toast.success(t("seo.imageUploaded"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setUploading(false);
    }
  };

  if (!seo) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  const ogPreview = seo.og_image_url || content?.logo_url || "/logo_transparent.png";

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,360px)] items-start w-full min-w-0" data-testid="admin-panel-seo">
      <div className="space-y-4 min-w-0">
        <Card className="rounded-2xl border border-border p-5 sm:p-6 space-y-5">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Search className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <h2 className="font-display text-lg sm:text-xl font-semibold">{t("seo.title")}</h2>
              <p className="text-sm text-muted-foreground mt-0.5">{t("seo.subtitle")}</p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 min-w-0 [&>*]:min-w-0">
            <div>
              <Label htmlFor="seo-site-name">{t("seo.siteName")}</Label>
              <Input id="seo-site-name" value={seo.site_name || ""} onChange={(e) => set("site_name", e.target.value)} className="mt-1.5 rounded-xl h-10" data-testid="seo-site-name" />
            </div>
            <div>
              <Label htmlFor="seo-canonical">{t("seo.canonical")}</Label>
              <Input id="seo-canonical" dir="ltr" placeholder="https://startupgate.sa" value={seo.canonical_base || ""} onChange={(e) => set("canonical_base", e.target.value)} className="mt-1.5 rounded-xl h-10 font-mono text-sm" data-testid="seo-canonical" />
              <p className="text-[11px] text-muted-foreground mt-1">{t("seo.canonicalHelp")}</p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 min-w-0 [&>*]:min-w-0">
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="seo-title-en">{t("seo.homeTitle")} (EN)</Label>
                <CharCount value={seo.home_title_en} lo={30} hi={60} />
              </div>
              <Input id="seo-title-en" value={seo.home_title_en || ""} onChange={(e) => set("home_title_en", e.target.value)} className="mt-1.5 rounded-xl h-10" data-testid="seo-title-en" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="seo-title-ar">{t("seo.homeTitle")} (AR)</Label>
                <CharCount value={seo.home_title_ar} lo={30} hi={60} />
              </div>
              <Input id="seo-title-ar" dir="rtl" value={seo.home_title_ar || ""} onChange={(e) => set("home_title_ar", e.target.value)} className="mt-1.5 rounded-xl h-10" data-testid="seo-title-ar" />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 min-w-0 [&>*]:min-w-0">
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="seo-desc-en">{t("seo.homeDesc")} (EN)</Label>
                <CharCount value={seo.home_desc_en} lo={70} hi={160} />
              </div>
              <textarea id="seo-desc-en" value={seo.home_desc_en || ""} onChange={(e) => set("home_desc_en", e.target.value)} className="mt-1.5 w-full min-w-0 rounded-xl border border-input bg-transparent px-3 py-2 text-sm min-h-24 resize-y" data-testid="seo-desc-en" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="seo-desc-ar">{t("seo.homeDesc")} (AR)</Label>
                <CharCount value={seo.home_desc_ar} lo={70} hi={160} />
              </div>
              <textarea id="seo-desc-ar" dir="rtl" value={seo.home_desc_ar || ""} onChange={(e) => set("home_desc_ar", e.target.value)} className="mt-1.5 w-full min-w-0 rounded-xl border border-input bg-transparent px-3 py-2 text-sm min-h-24 resize-y" data-testid="seo-desc-ar" />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 min-w-0 [&>*]:min-w-0">
            <div>
              <Label htmlFor="seo-kw-en">{t("seo.keywords")} (EN)</Label>
              <textarea id="seo-kw-en" value={seo.home_keywords_en || ""} onChange={(e) => set("home_keywords_en", e.target.value)} className="mt-1.5 w-full min-w-0 rounded-xl border border-input bg-transparent px-3 py-2 text-sm min-h-20 resize-y" placeholder={t("seo.keywordsHint")} data-testid="seo-keywords-en" />
            </div>
            <div>
              <Label htmlFor="seo-kw-ar">{t("seo.keywords")} (AR)</Label>
              <textarea id="seo-kw-ar" dir="rtl" value={seo.home_keywords_ar || ""} onChange={(e) => set("home_keywords_ar", e.target.value)} className="mt-1.5 w-full min-w-0 rounded-xl border border-input bg-transparent px-3 py-2 text-sm min-h-20 resize-y" data-testid="seo-keywords-ar" />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 items-start min-w-0 [&>*]:min-w-0">
            <div>
              <Label>{t("seo.ogImage")}</Label>
              <div className="mt-1.5 rounded-xl border border-border p-3 bg-muted/30 flex items-center gap-3">
                <img src={ogPreview} alt="Share preview" className="h-12 w-auto max-w-[120px] object-contain" data-testid="seo-og-preview" />
                <label className="cursor-pointer">
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadOg} data-testid="seo-og-upload-input" />
                  <Button asChild variant="outline" size="sm" className="rounded-lg gap-1.5" type="button">
                    <span>{uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {t("seo.upload")}</span>
                  </Button>
                </label>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">{t("seo.ogImageHelp")}</p>
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="seo-twitter">{t("seo.twitter")}</Label>
                <Input id="seo-twitter" dir="ltr" placeholder="@thesaudigate" value={seo.twitter_handle || ""} onChange={(e) => set("twitter_handle", e.target.value)} className="mt-1.5 rounded-xl h-10" data-testid="seo-twitter" />
              </div>
              <div className="flex items-center gap-3 rounded-xl border border-border p-3">
                <Switch id="seo-noindex" checked={!!seo.noindex_site} onCheckedChange={(v) => set("noindex_site", v)} data-testid="seo-noindex" />
                <div className="min-w-0">
                  <Label htmlFor="seo-noindex" className="cursor-pointer">{t("seo.noindex")}</Label>
                  <p className="text-[11px] text-muted-foreground">{t("seo.noindexHelp")}</p>
                </div>
              </div>
            </div>
          </div>

          <Button onClick={save} disabled={saving} className="btn-brand rounded-xl gap-2" data-testid="seo-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {t("common.save")}
          </Button>
        </Card>

        {/* Live SERP-style preview so copy edits are judged in context. */}
        <Card className="rounded-2xl border border-border p-5" data-testid="seo-serp-preview">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-3">{t("seo.preview")}</p>
          <div className="rounded-xl border border-border p-4 bg-background" dir="ltr">
            <p className="text-xs text-emerald-700 truncate">{seo.canonical_base || health?.sitemap_url?.replace("/api/sitemap.xml", "") || "https://startupgate.sa"}</p>
            <p className="text-[#1a0dab] text-lg leading-snug truncate">
              {seo.home_title_en || seo.site_name || "The Saudi Gate"}
            </p>
            <p className="text-sm text-muted-foreground line-clamp-2">{seo.home_desc_en}</p>
          </div>
        </Card>
      </div>

      <Card className="rounded-2xl border border-border p-5 min-w-0" data-testid="seo-health-panel">
        <div className="flex items-center gap-2">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">{t("seo.health")}</p>
          <Button variant="ghost" size="icon" className="ms-auto h-8 w-8 rounded-lg" onClick={loadHealth} disabled={checking} data-testid="seo-health-refresh">
            {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </Button>
        </div>

        {!health ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          <>
            <div className="flex items-end gap-3 mt-3">
              <p className="font-display text-4xl font-semibold tabular-nums" dir="ltr" data-testid="seo-health-score">{health.score}</p>
              <div className="pb-1.5 text-[11px] text-muted-foreground">
                <p>{t("seo.scoreLabel")}</p>
                <p className="font-mono" dir="ltr">
                  {health.passing} · {health.warnings} · {health.failures}
                </p>
                <p className="text-[10px]">
                  {t("seo.scoreBreakdown", { pass: health.passing, warn: health.warnings, fail: health.failures })}
                </p>
              </div>
            </div>
            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden mt-2">
              <div
                className={`h-full ${health.score >= 80 ? "bg-emerald-500" : health.score >= 50 ? "bg-amber-500" : "bg-rose-500"}`}
                style={{ width: `${health.score}%` }}
              />
            </div>

            <ul className="mt-4">
              {health.checks.map((c) => <HealthRow key={c.id} check={c} t={t} />)}
            </ul>

            <div className="mt-4 flex flex-col gap-1.5 text-xs">
              <a href="/api/sitemap.xml" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-primary hover:underline" data-testid="seo-sitemap-link">
                <ExternalLink className="h-3.5 w-3.5 rtl-flip" /> {t("seo.viewSitemap")} ({health.sitemap_urls})
              </a>
              <a href="/api/robots.txt" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-primary hover:underline" data-testid="seo-robots-link">
                <ExternalLink className="h-3.5 w-3.5 rtl-flip" /> {t("seo.viewRobots")}
              </a>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

export default AdminSeoPanel;
